import {
    Component,
    computed,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { Button } from 'primeng/button';
import { Toolbar } from 'primeng/toolbar';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Tag } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { SiteContentService } from '@/app/services/site-content.service';
import { RealmService } from '@/app/services/realm.service';
import { AuthService } from '@/app/services/auth.service';
import {
    CategoryCurrencyConfig,
    CategoryKycConfig,
    CategoryLegalConfigUpdate,
    CategoryType,
    CategoryWithdrawConfig,
    ContentDocument,
    FieldUpdate,
    PaymentMethod,
    ProviderStatus,
    RealmBrandingConfig,
    RealmCategorySpecificConfigUpdate,
    RealmConfig,
    RealmFineraConfig,
    RealmFineraWithdrawConfig,
    RealmGoogleConfig,
    RealmSupportConfig,
} from '@/lib-backend';
import { isSkillSpecific, overrideUpdate } from '@/app/utils/inheritance';
import {
    emptyLegalDraft,
    legalDraftFromDetails,
    LegalDocKey,
    LegalOverridesDraft,
} from '@/app/models/legal-documents';
import { GeneralTab } from './tabs/general-tab';
import { BrandingTab } from './tabs/branding-tab';
import { CurrencyTab } from './tabs/currency-tab';
import { WithdrawTab } from './tabs/withdraw-tab';
import { KycTab } from './tabs/kyc-tab';
import { GoogleTab } from './tabs/google-tab';
import { PaymentTab } from './tabs/payment-tab';
import { LegalTab } from './tabs/legal-tab';
import { FaqTab } from './tabs/faq-tab';
import { SocialTab } from './tabs/social-tab';
import { GamesTab } from './tabs/games-tab';
import { SiteTemplateTab } from './tabs/site-template-tab';

@Component({
    selector: 'app-website-info',
    standalone: true,
    imports: [
        Button,
        Toolbar,
        RouterLink,
        Tabs,
        Tab,
        TabList,
        TabPanels,
        TabPanel,
        Tag,
        Toast,
        GeneralTab,
        BrandingTab,
        CurrencyTab,
        WithdrawTab,
        KycTab,
        GoogleTab,
        PaymentTab,
        LegalTab,
        FaqTab,
        SocialTab,
        GamesTab,
        SiteTemplateTab,
    ],
    providers: [MessageService],
    templateUrl: './website-info.html',
    styleUrl: './website-info.scss',
})
export class WebsiteInfo implements OnChanges {
    @Input() realm: RealmConfig | null = null;
    @Input() providerStatus: ProviderStatus | null = null;
    @Output() close = new EventEmitter<void>();
    @Output() realmReassigned = new EventEmitter<RealmConfig>();

    protected readonly realmService = inject(RealmService);
    protected readonly siteContentService = inject(SiteContentService);
    private readonly messageService = inject(MessageService);
    private readonly authService = inject(AuthService);

    /** Site-template assignment (list + setRealmSiteTemplate) is global-admin only. */
    protected readonly canManageSiteTemplate = this.authService.isGlobalAdmin;

    readonly saving = signal(false);
    readonly saveError = signal<string | null>(null);

    /** Signal mirror of the @Input so computeds below stay reactive to realm changes. */
    private readonly realmSig = signal<RealmConfig | null>(null);

    protected readonly isSkill = computed(
        () =>
            this.realmSig()?.CategorySpecific.type ===
            CategoryType.SkillBasedGames,
    );

    brandingDraft = signal<RealmBrandingConfig>(emptyBranding());
    supportDraft = signal<RealmSupportConfig>({ SupportEmail: '' });
    googleDraft = signal<RealmGoogleConfig>({
        IsSignUpEnabled: false,
        ClientId: '',
    });
    paymentMethodDraft = signal<PaymentMethod>(PaymentMethod.None);

    fineraDraft = signal<RealmFineraConfig>(emptyFinera());
    fineraWithdrawDraft = signal<RealmFineraWithdrawConfig>(
        emptyFineraWithdraw(),
    );

    currencyDraft = signal<CategoryCurrencyConfig>(emptyCurrency());
    currencyOverridden = signal(false);
    withdrawDraft = signal<CategoryWithdrawConfig>(emptyWithdraw());
    withdrawOverridden = signal(false);
    kycDraft = signal<CategoryKycConfig>(emptyKyc());
    kycOverridden = signal(false);
    legalDraft = signal<LegalOverridesDraft>(emptyLegalDraft());

    // Original values for dirty comparison
    private brandingOriginal = signal<RealmBrandingConfig>(emptyBranding());
    private supportOriginal = signal<RealmSupportConfig>({ SupportEmail: '' });
    private googleOriginal = signal<RealmGoogleConfig>({
        IsSignUpEnabled: false,
        ClientId: '',
    });
    private paymentMethodOriginal = signal<PaymentMethod>(PaymentMethod.None);
    private fineraOriginal = signal<RealmFineraConfig>(emptyFinera());
    private fineraWithdrawOriginal = signal<RealmFineraWithdrawConfig>(
        emptyFineraWithdraw(),
    );
    private currencyOriginal = signal<CategoryCurrencyConfig>(emptyCurrency());
    private currencyOverriddenOriginal = signal(false);
    private withdrawOriginal = signal<CategoryWithdrawConfig>(emptyWithdraw());
    private withdrawOverriddenOriginal = signal(false);
    private kycOriginal = signal<CategoryKycConfig>(emptyKyc());
    private kycOverriddenOriginal = signal(false);
    private legalOriginal = signal<LegalOverridesDraft>(emptyLegalDraft());

    protected brandingDirty = computed(
        () =>
            JSON.stringify(this.brandingDraft()) !==
            JSON.stringify(this.brandingOriginal()),
    );
    protected generalDirty = computed(
        () =>
            JSON.stringify(this.supportDraft()) !==
            JSON.stringify(this.supportOriginal()),
    );
    protected googleDirty = computed(
        () =>
            JSON.stringify(this.googleDraft()) !==
            JSON.stringify(this.googleOriginal()),
    );
    protected paymentMethodDirty = computed(
        () => this.paymentMethodDraft() !== this.paymentMethodOriginal(),
    );
    protected fineraConfigDirty = computed(
        () =>
            JSON.stringify(this.fineraDraft()) !==
            JSON.stringify(this.fineraOriginal()),
    );
    protected fineraWithdrawDirty = computed(
        () =>
            this.isSkill() &&
            JSON.stringify(this.fineraWithdrawDraft()) !==
                JSON.stringify(this.fineraWithdrawOriginal()),
    );
    protected currencyDirty = computed(
        () =>
            this.currencyOverridden() !== this.currencyOverriddenOriginal() ||
            JSON.stringify(this.currencyDraft()) !==
                JSON.stringify(this.currencyOriginal()),
    );
    protected withdrawDirty = computed(
        () =>
            this.isSkill() &&
            (this.withdrawOverridden() !== this.withdrawOverriddenOriginal() ||
                JSON.stringify(this.withdrawDraft()) !==
                    JSON.stringify(this.withdrawOriginal())),
    );
    protected kycDirty = computed(
        () =>
            this.kycOverridden() !== this.kycOverriddenOriginal() ||
            JSON.stringify(this.kycDraft()) !==
                JSON.stringify(this.kycOriginal()),
    );
    protected legalDirty = computed(
        () =>
            JSON.stringify(this.legalDraft()) !==
            JSON.stringify(this.legalOriginal()),
    );

    /** Drives the "edited" badge on the Payment tab strip. */
    protected paymentDirty = computed(
        () =>
            this.fineraConfigDirty() ||
            this.fineraWithdrawDirty() ||
            this.paymentMethodDirty(),
    );
    private categoryDirty = computed(
        () =>
            this.currencyDirty() ||
            this.kycDirty() ||
            this.withdrawDirty() ||
            this.legalDirty(),
    );

    readonly hasUnsavedChanges = computed(
        () =>
            this.brandingDirty() ||
            this.generalDirty() ||
            this.googleDirty() ||
            this.fineraConfigDirty() ||
            this.fineraWithdrawDirty() ||
            this.paymentMethodDirty() ||
            this.categoryDirty(),
    );

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['realm'] || !this.realm) return;

        this.realmSig.set(this.realm);

        const previous = changes['realm'].previousValue as RealmConfig | null;
        const isSameRealm = previous?.RealmId === this.realm.RealmId;

        // Same realm, updated config (e.g. company reassignment): keep drafts —
        // they belong to the user's in-progress edits, which the surgical update
        // (CompanyId/Version only) does not touch.
        if (isSameRealm) return;

        this.seedFromRealm(this.realm);

        this.siteContentService.loadFaqsForRealm(this.realm.RealmId);
        this.siteContentService.loadSocialLinksForRealm(this.realm.RealmId);
    }

    private seedFromRealm(realm: RealmConfig): void {
        const cs = realm.CategorySpecific;

        const branding: RealmBrandingConfig = { ...realm.Branding };
        const support: RealmSupportConfig = { ...realm.Support };
        const google: RealmGoogleConfig = { ...realm.Google };
        const finera: RealmFineraConfig = {
            ...realm.Finera,
            Deposit: { ...realm.Finera.Deposit },
        };

        const currency = clone(cs.Currency.Effective);
        const currencyOverridden = cs.Currency.IsOverridden;
        const kyc = clone(cs.Kyc.Effective);
        const kycOverridden = cs.Kyc.IsOverridden;

        const skill = isSkillSpecific(cs);
        const withdraw = skill ? clone(cs.Withdraw.Effective) : emptyWithdraw();
        const withdrawOverridden = skill ? cs.Withdraw.IsOverridden : false;
        const fineraWithdraw = skill
            ? clone(cs.FineraWithdraw)
            : emptyFineraWithdraw();

        this.brandingDraft.set(branding);
        this.supportDraft.set(support);
        this.googleDraft.set(google);
        this.fineraDraft.set(finera);
        this.fineraWithdrawDraft.set(fineraWithdraw);
        this.paymentMethodDraft.set(realm.ActivePaymentMethod);
        this.currencyDraft.set(currency);
        this.currencyOverridden.set(currencyOverridden);
        this.withdrawDraft.set(withdraw);
        this.withdrawOverridden.set(withdrawOverridden);
        this.kycDraft.set(kyc);
        this.kycOverridden.set(kycOverridden);
        const legal = legalDraftFromDetails(cs.Legal);
        this.legalDraft.set(legal);

        this.brandingOriginal.set(clone(branding));
        this.supportOriginal.set(clone(support));
        this.googleOriginal.set(clone(google));
        this.fineraOriginal.set(clone(finera));
        this.fineraWithdrawOriginal.set(clone(fineraWithdraw));
        this.paymentMethodOriginal.set(realm.ActivePaymentMethod);
        this.currencyOriginal.set(clone(currency));
        this.currencyOverriddenOriginal.set(currencyOverridden);
        this.withdrawOriginal.set(clone(withdraw));
        this.withdrawOverriddenOriginal.set(withdrawOverridden);
        this.kycOriginal.set(clone(kyc));
        this.kycOverriddenOriginal.set(kycOverridden);
        this.legalOriginal.set(clone(legal));
    }

    /**
     * Per-document FieldUpdate semantics: untouched document → null (no
     * change); touched and overridden → set the draft document; touched and
     * not overridden → { value: null } (clear the override, fall back to the
     * category template).
     */
    private buildLegalUpdate(): CategoryLegalConfigUpdate | null {
        if (!this.legalDirty()) return null;
        const draft = this.legalDraft();
        const original = this.legalOriginal();
        const update = (
            key: LegalDocKey,
        ): FieldUpdate<ContentDocument | null> => {
            if (JSON.stringify(draft[key]) === JSON.stringify(original[key]))
                return null;
            return { value: draft[key].overridden ? draft[key].doc : null };
        };
        return {
            TermsOfServiceOverride: update('TermsOfService'),
            PrivacyPolicyOverride: update('PrivacyPolicy'),
            AmlPolicyOverride: update('AmlPolicy'),
            CookiesPolicyOverride: update('CookiesPolicy'),
            RefundPolicyOverride: update('RefundPolicy'),
            DisclaimerOverride: update('Disclaimer'),
            AboutDescriptionOverride: update('AboutDescription'),
        };
    }

    private buildCategoryUpdate(): RealmCategorySpecificConfigUpdate | null {
        if (!this.realm || !this.categoryDirty()) return null;

        const kycOverride = overrideUpdate(
            this.kycDirty(),
            this.kycOverridden(),
            this.kycDraft(),
        );
        const currencyOverride = overrideUpdate(
            this.currencyDirty(),
            this.currencyOverridden(),
            this.currencyDraft(),
        );

        if (isSkillSpecific(this.realm.CategorySpecific)) {
            return {
                type: CategoryType.SkillBasedGames,
                KycOverride: kycOverride,
                WithdrawOverride: overrideUpdate(
                    this.withdrawDirty(),
                    this.withdrawOverridden(),
                    this.withdrawDraft(),
                ),
                CurrencyOverride: currencyOverride,
                LegalOverride: this.buildLegalUpdate(),
                FineraWithdraw: this.fineraWithdrawDirty()
                    ? this.fineraWithdrawDraft()
                    : null,
            };
        }

        return {
            type: CategoryType.LuckyBasedGames,
            KycOverride: kycOverride,
            CurrencyOverride: currencyOverride,
            LegalOverride: this.buildLegalUpdate(),
        };
    }

    async saveAll(): Promise<void> {
        if (!this.realm) return;
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const result = await this.realmService.updateRealm({
                RealmId: this.realm.RealmId,
                Branding: this.brandingDirty() ? this.brandingDraft() : null,
                Support: this.generalDirty() ? this.supportDraft() : null,
                Google: this.googleDirty() ? this.googleDraft() : null,
                Finera: this.fineraConfigDirty() ? this.fineraDraft() : null,
                ActivePaymentMethod: this.paymentMethodDirty()
                    ? this.paymentMethodDraft()
                    : null,
                Category: this.buildCategoryUpdate(),
            });
            if (result === 'InvalidActivePaymentMethod') {
                this.saveError.set(
                    'Selected payment method is not configured. Enable it first.',
                );
            } else if (result !== 'Ok') {
                this.saveError.set(`Save failed: ${result}`);
            } else {
                // Re-fetch so inheritance metadata (Effective / Inherited / IsOverridden) is authoritative.
                const updated = await this.realmService.getRealm(
                    this.realm.RealmId,
                );
                if (updated) {
                    this.realm = updated.config;
                    this.realmSig.set(updated.config);
                    this.seedFromRealm(updated.config);
                }
                this.messageService.add({
                    severity: 'success',
                    summary: 'Saved',
                    detail: 'Settings saved successfully.',
                });
            }
        } finally {
            this.saving.set(false);
        }
    }

    protected backToList(): void {
        this.close.emit();
    }
}

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}

function emptyBranding(): RealmBrandingConfig {
    return {
        Name: '',
        FullName: '',
        Primary: '',
        Secondary: '',
        DisplayName: '',
        Description: '',
        FaviconUrl: null,
        OgImageUrl: null,
        LogoIconUrl: null,
    };
}

function emptyFinera(): RealmFineraConfig {
    return {
        IsEnabled: false,
        BaseUrl: '',
        Deposit: { GroupId: '', ControlKey: '' },
    };
}

function emptyFineraWithdraw(): RealmFineraWithdrawConfig {
    return {
        GroupId: '',
        ControlKey: '',
        ConnectingPartyName: '',
        RsaPrivateKeyPem: '',
    };
}

function emptyCurrency(): CategoryCurrencyConfig {
    return {
        CreditsPerDollar: { FreeCredits: 0, PaidCredits: 0 },
        FreeCurrencyType: '',
        PaidCurrencyType: '',
        InitialFunds: { FreeCredits: 0, PaidCredits: 0 },
        ShowFreeCurrency: true,
        ShowPaidCurrency: false,
    };
}

function emptyWithdraw(): CategoryWithdrawConfig {
    return {
        WithdrawEnabled: false,
        MinWithdrawAmountUsd: 0,
        WithdrawCreditsPerDollar: 0,
    };
}

function emptyKyc(): CategoryKycConfig {
    return { RequireForDeposit: false, RequireForWithdrawal: false };
}
